// lib/muse-image.ts

export const MUSE_IMAGE_MAX_INPUT_IMAGES = 10;

/** Muse reads its input within 2048 px per side, whatever the about 2.5 Mpx it returns (owner, 2026-10-15). */
export const MUSE_IMAGE_MAX_INPUT_SIDE = 2048;

/** The render budget that puts a width:height input's long side at MUSE_IMAGE_MAX_INPUT_SIDE. */
export const museImageInputPixels = (width: number, height: number): number => (
  width > 0 && height > 0
    ? Math.floor((MUSE_IMAGE_MAX_INPUT_SIDE ** 2 * Math.min(width, height)) / Math.max(width, height))
    : MUSE_IMAGE_MAX_INPUT_SIDE ** 2
);

export const MUSE_IMAGE_MAX_RATIO = 16;
const MAX_TERM = 32;
// fal's documented presets, sent as written when the shape is within 0.1 % of one (21:9 rather than 7:3).
const PRESETS = ['21:9', '16:9', '4:3', '3:2', '1:1', '2:3', '3:4', '9:16', '9:21'];
const PRESET_LOG_TOLERANCE = 0.001;

/** width:height clamped to the provider's 1:16..16:1, as the closest pair of terms up to 32. */
export const deriveMuseImageAspectRatio = (width: number, height: number): string => {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return '1:1';
  const target = Math.log(Math.min(MUSE_IMAGE_MAX_RATIO, Math.max(1 / MUSE_IMAGE_MAX_RATIO, width / height)));
  const preset = PRESETS.find((ratio) => {
    const [w = 1, h = 1] = ratio.split(':').map(Number);
    return Math.abs(Math.log(w / h) - target) <= PRESET_LOG_TOLERANCE;
  });
  if (preset) return preset;
  let best = '1:1';
  let bestError = Infinity;
  for (let h = 1; h <= MAX_TERM; h++) {
    for (let w = 1; w <= MAX_TERM; w++) {
      const error = Math.abs(Math.log(w / h) - target);
      if (error < bestError) {
        best = `${w}:${h}`;
        bestError = error;
      }
    }
  }
  return best;
};

/** A "width:height" string, normalized and clamped like a derived one; null when it is not one. */
export const parseMuseImageAspectRatio = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const match = /^\s*(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)\s*$/.exec(value);
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  return width > 0 && height > 0 ? deriveMuseImageAspectRatio(width, height) : null;
};

export const getMuseImageEndpoint = (hasSource: boolean): string => (
  `meta/muse-image/${hasSource ? 'edit' : 'text-to-image'}`
);

export const buildMuseImageProviderInput = (input: {
  prompt: string;
  aspectRatio: string;
  imageUrls?: readonly string[];
}) => {
  const imageUrls = (input.imageUrls ?? [])
    .filter((url, index, urls): url is string => typeof url === 'string' && url.trim().length > 0 && urls.indexOf(url) === index)
    .slice(0, MUSE_IMAGE_MAX_INPUT_IMAGES);
  return {
    prompt: input.prompt,
    // Studio owns batching (one task per variant), so the provider always returns a single image.
    num_images: 1,
    // Never 'auto': with up to ten inputs nothing says which one it would follow.
    aspect_ratio: input.aspectRatio,
    output_format: 'webp' as const,
    // A data-URI response would blow up the fal.subscribe payload.
    sync_mode: false,
    ...(imageUrls.length > 0 ? { image_urls: imageUrls } : {}),
  };
};
